import { Alert, Anchor, Breadcrumbs, Button, Card, Group, List, Stack, Text, Textarea, Title } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import FlowProposalBadges, { FLOW_PROPOSAL_STATUSES } from '../../components/FlowProposals/FlowProposalBadges';
import FlowProposalDiff from '../../components/FlowProposals/FlowProposalDiff';
import Metadata from '../../components/Metadata';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import Spinner from '../../components/Spinner';
import StepBreakdown from '../../components/Stats/StepBreakdown';
import { CampaignTypes, Page } from '../../constants/pageTitles';
import { daysAgoISODate, todayISODate } from '../../helpers/dateUtils';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';

const FLOW_CHANGED_MESSAGE = 'The flow was edited after this proposal was made. It can no longer be applied.';
const UNDISPLAYABLE_FLOW_MESSAGE =
  'The proposed flow could not be displayed, so it cannot be approved. It can still be rejected.';

const FlowProposalDetails = () => {
  const fetch = useFetch();
  const { accountId, domainId, proposalId } = useParams();
  const [proposal, setProposal] = useState();
  const [loadError, setLoadError] = useState();
  const [note, setNote] = useState('');
  const [decision, setDecision] = useState(null);
  const [deciding, setDeciding] = useState(false);
  const [decisionError, setDecisionError] = useState();
  const [flowChanged, setFlowChanged] = useState(false);
  const [proposedFlowBroken, setProposedFlowBroken] = useState(false);

  const domainPath = `/accounts/${accountId}/domains/${domainId}`;
  const proposalUrl = `/api/admin${domainPath}/flowProposals/${proposalId}`;

  const load = useCallback(() => {
    fetch(proposalUrl)
      .then(({ proposal }) => setProposal(proposal))
      .catch(setLoadError);
  }, [fetch, proposalUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = (close) => {
    setDeciding(true);
    setDecisionError(undefined);
    fetch(`${proposalUrl}/${decision}`, { method: 'POST', body: JSON.stringify({ note: note.trim() || undefined }) })
      .then(({ proposal: decided }) => {
        setProposal((prev) => ({ ...prev, ...decided }));
        close();
      })
      .catch((err) => {
        if (err?.code === 'FLOW_CHANGED') {
          setFlowChanged(true);
          close();
          load();
        } else {
          setDecisionError(new Error(getErrorMessage(err)));
          load();
        }
      })
      .finally(() => setDeciding(false));
  };

  if (loadError && !proposal) {
    return <ErrorMessage message={getErrorMessage(loadError)} />;
  }

  if (!proposal) {
    return <Spinner />;
  }

  const { campaign } = proposal;
  const isPending = proposal.status === 'pending';
  const undisplayable = isPending && proposedFlowBroken;
  const flowOutdated = flowChanged || proposal.status === 'conflicted' || (isPending && proposal.stale);
  const cannotApply = undisplayable || flowOutdated;
  const settings = proposal.proposedSettings || {};
  const fieldChanges = [
    settings.segmentId != null && `Segment: change to segment ID ${settings.segmentId}`,
    settings.mode && `Mode: change to ${CampaignTypes[settings.mode] || settings.mode}`,
    'scheduledAt' in settings &&
      `Schedule: change to ${settings.scheduledAt ? new Date(settings.scheduledAt).toLocaleString() : 'Not scheduled'}`,
  ].filter(Boolean);

  return (
    <>
      <Metadata title={`${Page.flowProposals} | ${campaign.name}`} />
      <Breadcrumbs mb="md">
        <Anchor href={`#${domainPath}/analytics`}>Home</Anchor>
        <Anchor href={`#${domainPath}/flowProposals`}>{Page.flowProposals}</Anchor>
        <Text>{campaign.name}</Text>
      </Breadcrumbs>

      <Stack gap="md">
        <div>
          <Group gap="sm" mb={4}>
            <Title order={3}>
              Proposed change to{' '}
              <Anchor href={`#${domainPath}/workflows/${campaign.id}/details`} inherit>
                {campaign.name}
              </Anchor>
            </Title>
            <FlowProposalBadges proposal={proposal} />
          </Group>
          <Text size="sm" c="dimmed">
            Proposed by {proposal.proposedBy?.email || 'AI assistant'} on{' '}
            {new Date(proposal.createdAt).toLocaleString()}
          </Text>
          {proposal.decidedAt && (
            <Text size="sm" c="dimmed">
              {FLOW_PROPOSAL_STATUSES[proposal.status].label}
              {proposal.decidedBy && ` by ${proposal.decidedBy.email}`} on{' '}
              {new Date(proposal.decidedAt).toLocaleString()}
            </Text>
          )}
        </div>

        {flowOutdated && (
          <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
            {FLOW_CHANGED_MESSAGE}
          </Alert>
        )}
        {undisplayable && (
          <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
            {UNDISPLAYABLE_FLOW_MESSAGE}
          </Alert>
        )}

        <Card withBorder>
          <Title order={5} mb="xs">
            Rationale
          </Title>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {proposal.rationale}
          </Text>
          {proposal.decisionNote && (
            <>
              <Title order={5} mt="md" mb="xs">
                Decision note
              </Title>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {proposal.decisionNote}
              </Text>
            </>
          )}
        </Card>

        {fieldChanges.length > 0 && (
          <Card withBorder>
            <Title order={5} mb="xs">
              Workflow settings
            </Title>
            <List size="sm" spacing={4}>
              {fieldChanges.map((change) => (
                <List.Item key={change}>{change}</List.Item>
              ))}
            </List>
          </Card>
        )}

        <FlowProposalDiff
          currentFlow={proposal.currentFlow}
          proposedFlow={proposal.proposedFlow}
          onProposedFlowError={() => setProposedFlowBroken(true)}
        />

        {isPending && (
          <Card withBorder>
            <Textarea
              label="Decision note"
              description="Optional. Saved with your decision."
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              autosize
              minRows={2}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" leftSection={<IconX size={16} />} onClick={() => setDecision('reject')}>
                Reject
              </Button>
              <Button
                leftSection={<IconCheck size={16} />}
                disabled={cannotApply}
                onClick={() => setDecision('approve')}
              >
                Approve
              </Button>
            </Group>
          </Card>
        )}

        <div>
          <Title order={5} mb="xs">
            Step stats for the current flow, last 30 days
          </Title>
          <StepBreakdown
            campaign={{ id: campaign.id, flow: proposal.currentFlow }}
            startDate={daysAgoISODate(29)}
            endDate={todayISODate()}
          />
        </div>
      </Stack>

      <ConfirmationModal
        show={decision !== null}
        close={() => {
          setDecision(null);
          setDecisionError(undefined);
        }}
        confirm={decide}
        loading={deciding}
        error={decisionError}
        message={
          {
            approve: (
              <>
                This replaces the flow of {campaign.name} immediately.
                {campaign.running && (
                  <>
                    <br />
                    This workflow is running, so the change also affects contacts who are already in the flow.
                  </>
                )}
              </>
            ),
            reject: `The proposal is rejected and the flow of ${campaign.name} stays as it is.`,
          }[decision]
        }
      />
    </>
  );
};

const FlowProposalDetailsPage = () => {
  const { proposalId } = useParams();
  return <FlowProposalDetails key={proposalId} />;
};

export default FlowProposalDetailsPage;
